/**
 * API contracts for the `production` module — zod schemas + inferred types shared by
 * apps/api, apps/web and (via generated types) apps/android.
 *
 * OWNERSHIP: the production module agent owns this file exclusively. It is
 * pre-created (and pre-exported from ./index.ts) by the scaffold so filling it
 * in never requires touching a shared file. Import from '@warehouse/shared'.
 *
 * Scope: spec §5.9 (production orders, goods-to-production, task board, goods
 * requests). §5.10 production REPORTS are deliberately absent — they are a later
 * wave and would need report tables this schema does not have.
 *
 * WMS-first (MODULES.md §M6): orders are created and driven here; Business
 * Central is optional and lives behind the `PRODUCTION_SYNC_BC` config flag, so
 * every command in this file works with `ERP_ADAPTER=null`.
 */
import { z } from 'zod';

import { listResponseSchema, qtySchema, uuidSchema } from '../schemas/common.js';

// ---------------------------------------------------------------------------
// enums (mirror db/schema/production.ts — the pgEnum values, verbatim)
// ---------------------------------------------------------------------------

export const productionOrderStatusSchema = z.enum([
  'planned',
  'released',
  'in_progress',
  'done',
  'cancelled',
]);
export type ProductionOrderStatus = z.infer<typeof productionOrderStatusSchema>;

export const productionOrderSourceSchema = z.enum(['wms', 'bc']);
export type ProductionOrderSource = z.infer<typeof productionOrderSourceSchema>;

export const productionTaskKindSchema = z.enum(['unload', 'move_to_production', 'general']);
export type ProductionTaskKind = z.infer<typeof productionTaskKindSchema>;

export const productionTaskStatusSchema = z.enum(['open', 'in_progress', 'done']);
export type ProductionTaskStatus = z.infer<typeof productionTaskStatusSchema>;

export const productionRequestStatusSchema = z.enum([
  'requested',
  'accepted',
  'delivered',
  'rejected',
]);
export type ProductionRequestStatus = z.infer<typeof productionRequestStatusSchema>;

/**
 * Which transitions the order state machine allows. Enforced server-side in the
 * command handlers; the web uses it to decide which buttons to render.
 *
 *   planned ─release→ released ─start→ in_progress ─complete(full)→ done
 *                        └──────── complete(full) ────────────────┘
 * `cancelled` is reachable from anything that is not finished.
 */
export const PRODUCTION_ORDER_TRANSITIONS: Readonly<
  Record<ProductionOrderStatus, readonly ProductionOrderStatus[]>
> = {
  planned: ['released', 'cancelled'],
  released: ['in_progress', 'done', 'cancelled'],
  in_progress: ['done', 'cancelled'],
  done: [],
  cancelled: [],
};

export function canTransitionProductionOrder(
  from: ProductionOrderStatus,
  to: ProductionOrderStatus,
): boolean {
  return PRODUCTION_ORDER_TRANSITIONS[from].includes(to);
}

// ---------------------------------------------------------------------------
// command types — registered with the CommandBus so the SAME type works from
// the web and from `POST /api/sync/commands` (Android outbox, ARCHITECTURE §6).
// ---------------------------------------------------------------------------

export const PRODUCTION_COMMANDS = {
  orderCreate: 'production.order.create',
  orderUpdate: 'production.order.update',
  orderSetComponents: 'production.order.setComponents',
  orderRelease: 'production.order.release',
  orderStart: 'production.order.start',
  orderCancel: 'production.order.cancel',
  orderPlanStaging: 'production.order.planStaging',
  orderComplete: 'production.order.complete',
  componentStage: 'production.component.stage',
  taskCreate: 'production.task.create',
  taskUpdate: 'production.task.update',
  taskComplete: 'production.task.complete',
  requestCreate: 'production.request.create',
  requestAccept: 'production.request.accept',
  requestReject: 'production.request.reject',
  requestDeliver: 'production.request.deliver',
} as const;

export type ProductionCommandType =
  (typeof PRODUCTION_COMMANDS)[keyof typeof PRODUCTION_COMMANDS];

export const PRODUCTION_COMMAND_TYPES: readonly ProductionCommandType[] =
  Object.values(PRODUCTION_COMMANDS);

// ---------------------------------------------------------------------------
// production orders
// ---------------------------------------------------------------------------

const noteSchema = z.string().trim().max(2000).nullish();
/** `YYYY-MM-DD`; production plans are day-grained, never wall-clock. */
const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

export const productionComponentInputSchema = z.object({
  articleId: uuidSchema,
  qtyBase: qtySchema,
  sort: z.number().int().min(0).max(9999).optional(),
});
export type ProductionComponentInput = z.infer<typeof productionComponentInputSchema>;

export const productionOrderCreateSchema = z.object({
  /** Omit to let the server allocate `PROD-<yyyy>-<seq>`. */
  orderNo: z.string().trim().min(1).max(60).optional(),
  outputArticleId: uuidSchema,
  qtyBase: qtySchema,
  plannedStart: dateOnlySchema.nullish(),
  /** Which production floor the work happens on (`warehouse_areas.id`). */
  floorAreaId: uuidSchema.nullish(),
  note: noteSchema,
  /** Manual BOM now; the BC BOM fills these in later via erp_inbox. */
  components: z.array(productionComponentInputSchema).max(200).optional(),
});
export type ProductionOrderCreatePayload = z.infer<typeof productionOrderCreateSchema>;

export const productionOrderUpdateSchema = z.object({
  productionOrderId: uuidSchema,
  qtyBase: qtySchema.optional(),
  plannedStart: dateOnlySchema.nullish(),
  floorAreaId: uuidSchema.nullish(),
  note: noteSchema,
});
export type ProductionOrderUpdatePayload = z.infer<typeof productionOrderUpdateSchema>;

export const productionOrderSetComponentsSchema = z.object({
  productionOrderId: uuidSchema,
  components: z.array(productionComponentInputSchema).max(200),
});
export type ProductionOrderSetComponentsPayload = z.infer<
  typeof productionOrderSetComponentsSchema
>;

export const productionOrderIdSchema = z.object({ productionOrderId: uuidSchema });
export type ProductionOrderIdPayload = z.infer<typeof productionOrderIdSchema>;

export const productionOrderCancelSchema = z.object({
  productionOrderId: uuidSchema,
  reason: z.string().trim().max(500).nullish(),
});
export type ProductionOrderCancelPayload = z.infer<typeof productionOrderCancelSchema>;

/**
 * "Goods to production": turn every component with an outstanding quantity into
 * a `move_to_production` task on the board (spec §5.9 "tasks for when to unload
 * goods"). Idempotent — a component that already has an open task is skipped.
 */
export const productionOrderPlanStagingSchema = z.object({
  productionOrderId: uuidSchema,
  dueAt: z.iso.datetime().nullish(),
  assignedRole: z.string().trim().max(150).nullish(),
  assignedUser: uuidSchema.nullish(),
});
export type ProductionOrderPlanStagingPayload = z.infer<typeof productionOrderPlanStagingSchema>;

/**
 * Move ONE component from the warehouse onto the production floor bin.
 * Scanner-facing: the device sends its observed source bin/pallet/lot and the
 * server validates against current state (ARCHITECTURE §6).
 */
export const productionComponentStageSchema = z.object({
  productionOrderId: uuidSchema,
  componentId: uuidSchema,
  qtyBase: qtySchema,
  /** Omit to let the server pick FEFO across the warehouse. */
  fromBinId: uuidSchema.nullish(),
  lotId: uuidSchema.nullish(),
  palletId: uuidSchema.nullish(),
  /** Omit to use the order's production floor bin (app_settings-configurable). */
  toBinId: uuidSchema.nullish(),
  /** Close this board task when the move lands. */
  taskId: uuidSchema.nullish(),
});
export type ProductionComponentStagePayload = z.infer<typeof productionComponentStageSchema>;

export const productionConsumptionInputSchema = z.object({
  articleId: uuidSchema,
  qtyBase: qtySchema,
  lotId: uuidSchema.nullish(),
  palletId: uuidSchema.nullish(),
  /** Omit to consume from the order's production floor bin. */
  fromBinId: uuidSchema.nullish(),
});
export type ProductionConsumptionInput = z.infer<typeof productionConsumptionInputSchema>;

/**
 * Report output. Creates the output lot (`getOrCreateLot`), a `produce`
 * movement, and `consume` movements for the components — all in ONE
 * transaction, so the batch link between finished goods and the lots that went
 * into them can never be half-written.
 */
export const productionOrderCompleteSchema = z.object({
  productionOrderId: uuidSchema,
  producedQtyBase: qtySchema,
  /** Output batch number. Defaults to the order number (old-WMS label habit). */
  lotNo: z.string().trim().min(1).max(80).optional(),
  bestBefore: dateOnlySchema.nullish(),
  cartons: z.number().int().min(0).max(100000).nullish(),
  /** Where the finished goods land. Defaults to the production floor bin. */
  toBinId: uuidSchema.nullish(),
  /** Put the output on a fresh LPN pallet (label print follows in M9). */
  createPallet: z.boolean().optional(),
  /**
   * What was actually consumed. Omit (or leave empty) with `autoConsume` to let
   * the server take the components FEFO off the production floor.
   */
  consume: z.array(productionConsumptionInputSchema).max(200).optional(),
  /** Default true: derive consumption from the component list, FEFO. */
  autoConsume: z.boolean().optional(),
  note: noteSchema,
});
export type ProductionOrderCompletePayload = z.infer<typeof productionOrderCompleteSchema>;

// --- read models -----------------------------------------------------------

export const productionOrderComponentSchema = z.object({
  id: uuidSchema,
  productionOrderId: uuidSchema,
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  unitCode: z.string().nullable(),
  qtyBase: z.string(),
  issuedQtyBase: z.string(),
  /** `qtyBase - issuedQtyBase`, floored at 0 — what still has to be staged. */
  outstandingQtyBase: z.string(),
  sort: z.number().int(),
});
export type ProductionOrderComponent = z.infer<typeof productionOrderComponentSchema>;

export const productionOrderListItemSchema = z.object({
  id: uuidSchema,
  orderNo: z.string(),
  outputArticleId: uuidSchema,
  outputArticleNo: z.string(),
  outputArticleName: z.string(),
  unitCode: z.string().nullable(),
  qtyBase: z.string(),
  producedQtyBase: z.string(),
  status: productionOrderStatusSchema,
  source: productionOrderSourceSchema,
  plannedStart: z.string().nullable(),
  floorAreaId: uuidSchema.nullable(),
  floorAreaName: z.string().nullable(),
  note: z.string().nullable(),
  componentCount: z.number().int(),
  openTaskCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProductionOrderListItem = z.infer<typeof productionOrderListItemSchema>;

export const productionOutputSchema = z.object({
  id: uuidSchema,
  productionOrderId: uuidSchema,
  lotId: uuidSchema,
  lotNo: z.string(),
  bestBefore: z.string().nullable(),
  qtyBase: z.string(),
  cartons: z.number().int().nullable(),
  producedAt: z.string(),
  byUser: uuidSchema.nullable(),
  byUserName: z.string().nullable(),
});
export type ProductionOutput = z.infer<typeof productionOutputSchema>;

export const productionOrderDetailSchema = productionOrderListItemSchema.extend({
  components: z.array(productionOrderComponentSchema),
  outputs: z.array(productionOutputSchema),
  /** The floor bin components are staged to / consumed from, once resolvable. */
  floorBinId: uuidSchema.nullable(),
  floorBinCode: z.string().nullable(),
});
export type ProductionOrderDetail = z.infer<typeof productionOrderDetailSchema>;

export const productionOrderListResponseSchema = listResponseSchema(productionOrderListItemSchema);
export type ProductionOrderListResponse = z.infer<typeof productionOrderListResponseSchema>;

/**
 * The traceability answer (spec §2 "full traceability"): which component lots
 * went into which output lot, reconstructed from `stock_movements` — the spine,
 * not a denormalised copy that can drift.
 */
export const productionTraceabilitySchema = z.object({
  productionOrderId: uuidSchema,
  orderNo: z.string(),
  outputs: z.array(
    productionOutputSchema.extend({
      articleId: uuidSchema,
      articleNo: z.string(),
      articleName: z.string(),
    }),
  ),
  consumed: z.array(
    z.object({
      movementId: uuidSchema,
      at: z.string(),
      articleId: uuidSchema,
      articleNo: z.string(),
      articleName: z.string(),
      lotId: uuidSchema.nullable(),
      lotNo: z.string().nullable(),
      bestBefore: z.string().nullable(),
      binCode: z.string().nullable(),
      qtyBase: z.string(),
    }),
  ),
});
export type ProductionTraceability = z.infer<typeof productionTraceabilitySchema>;

// ---------------------------------------------------------------------------
// production tasks (the board)
// ---------------------------------------------------------------------------

export const productionTaskCreateSchema = z.object({
  productionOrderId: uuidSchema.nullish(),
  kind: productionTaskKindSchema.default('general'),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullish(),
  assignedRole: z.string().trim().max(150).nullish(),
  assignedUser: uuidSchema.nullish(),
  dueAt: z.iso.datetime().nullish(),
});
export type ProductionTaskCreatePayload = z.infer<typeof productionTaskCreateSchema>;

export const productionTaskUpdateSchema = z.object({
  taskId: uuidSchema,
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).nullish(),
  status: productionTaskStatusSchema.optional(),
  assignedRole: z.string().trim().max(150).nullish(),
  assignedUser: uuidSchema.nullish(),
  dueAt: z.iso.datetime().nullish(),
});
export type ProductionTaskUpdatePayload = z.infer<typeof productionTaskUpdateSchema>;

export const productionTaskCompleteSchema = z.object({ taskId: uuidSchema });
export type ProductionTaskCompletePayload = z.infer<typeof productionTaskCompleteSchema>;

export const productionTaskListItemSchema = z.object({
  id: uuidSchema,
  productionOrderId: uuidSchema.nullable(),
  productionOrderNo: z.string().nullable(),
  kind: productionTaskKindSchema,
  title: z.string(),
  description: z.string().nullable(),
  status: productionTaskStatusSchema,
  assignedRole: z.string().nullable(),
  assignedUser: uuidSchema.nullable(),
  assignedUserName: z.string().nullable(),
  dueAt: z.string().nullable(),
  createdBy: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProductionTaskListItem = z.infer<typeof productionTaskListItemSchema>;

export const productionTaskListResponseSchema = listResponseSchema(productionTaskListItemSchema);
export type ProductionTaskListResponse = z.infer<typeof productionTaskListResponseSchema>;

// ---------------------------------------------------------------------------
// production requests (the kanban)
// ---------------------------------------------------------------------------

export const productionRequestCreateSchema = z.object({
  articleId: uuidSchema,
  qtyBase: qtySchema,
  neededAt: z.iso.datetime().nullish(),
  note: noteSchema,
});
export type ProductionRequestCreatePayload = z.infer<typeof productionRequestCreateSchema>;

export const productionRequestAcceptSchema = z.object({
  requestId: uuidSchema,
  /** Where the warehouse should deliver it; defaults to the production floor bin. */
  toBinId: uuidSchema.nullish(),
  dueAt: z.iso.datetime().nullish(),
  assignedUser: uuidSchema.nullish(),
  note: noteSchema,
});
export type ProductionRequestAcceptPayload = z.infer<typeof productionRequestAcceptSchema>;

export const productionRequestRejectSchema = z.object({
  requestId: uuidSchema,
  note: noteSchema,
});
export type ProductionRequestRejectPayload = z.infer<typeof productionRequestRejectSchema>;

/** Scanner-facing: the actual warehouse → production-floor move. */
export const productionRequestDeliverSchema = z.object({
  requestId: uuidSchema,
  /** Defaults to the requested quantity. */
  qtyBase: qtySchema.optional(),
  fromBinId: uuidSchema.nullish(),
  toBinId: uuidSchema.nullish(),
  lotId: uuidSchema.nullish(),
  palletId: uuidSchema.nullish(),
});
export type ProductionRequestDeliverPayload = z.infer<typeof productionRequestDeliverSchema>;

export const productionRequestListItemSchema = z.object({
  id: uuidSchema,
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  unitCode: z.string().nullable(),
  qtyBase: z.string(),
  status: productionRequestStatusSchema,
  neededAt: z.string().nullable(),
  note: z.string().nullable(),
  requestedBy: uuidSchema.nullable(),
  requestedByName: z.string().nullable(),
  /** The `move_to_production` task the warehouse got when it accepted. */
  taskId: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProductionRequestListItem = z.infer<typeof productionRequestListItemSchema>;

export const productionRequestListResponseSchema = listResponseSchema(
  productionRequestListItemSchema,
);
export type ProductionRequestListResponse = z.infer<typeof productionRequestListResponseSchema>;

// ---------------------------------------------------------------------------
// production floor configuration
// ---------------------------------------------------------------------------

/**
 * `app_settings` keys this module reads. The old WMS hardcoded the floor bins
 * `KG-1A` / `LG-1A` in a service (recon: production-portal-claims.md A1) —
 * ARCHITECTURE §12 says that belongs in `app_settings`, so it does.
 */
export const PRODUCTION_SETTING_KEYS = {
  /** Bin codes usable as production floor bins, in preference order. */
  floorBinCodes: 'production_floor_bin_codes',
  /** Prefix for server-allocated production order numbers. */
  orderNoPrefix: 'production_order_no_prefix',
} as const;

/** Ported verbatim from the old WMS's hardcoded constants — now only a default. */
export const PRODUCTION_FLOOR_BIN_CODES_DEFAULT = ['KG-1A', 'LG-1A'] as const;
export const PRODUCTION_ORDER_NO_PREFIX_DEFAULT = 'PROD';

export const productionFloorBinSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  areaId: uuidSchema,
  areaCode: z.string(),
  areaName: z.string(),
  isBlocked: z.boolean(),
  /** How the bin was resolved — configured setting, floor area, or fallback. */
  source: z.enum(['area', 'setting', 'floor_role']),
});
export type ProductionFloorBin = z.infer<typeof productionFloorBinSchema>;

export const productionFloorBinsResponseSchema = z.object({
  bins: z.array(productionFloorBinSchema),
  configuredCodes: z.array(z.string()),
});
export type ProductionFloorBinsResponse = z.infer<typeof productionFloorBinsResponseSchema>;
